import { useEffect, useMemo, useRef, useState } from "react";

import { useRouter } from "next/router";
import { posthog as posthogClient } from "posthog-js";
import { useFeatureFlagVariantKey } from "posthog-js/react";

import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { captureCheckoutPromoFromQuery } from "@musicfy/utils/checkoutPromo";
import {
  CHECKOUT_PROVIDER_FLAG_KEY,
  CHECKOUT_PROVIDER_FLAG_WAIT_MS,
  CHECKOUT_PROVIDER_STORAGE_KEY,
  type CheckoutProvider,
  isCheckoutProviderReady,
  parseCheckoutProviderParam,
  resolveCheckoutProvider,
  shouldPersistCheckoutProvider,
} from "@musicfy/utils/checkoutProvider";

const readStickyProvider = (): CheckoutProvider | null => {
  if (typeof window === "undefined") return null;
  try {
    return parseCheckoutProviderParam(
      window.localStorage.getItem(CHECKOUT_PROVIDER_STORAGE_KEY)
    );
  } catch {
    return null;
  }
};

const writeStickyProvider = (provider: CheckoutProvider): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CHECKOUT_PROVIDER_STORAGE_KEY, provider);
  } catch {
    // ignore quota / private mode
  }
};

/**
 * A/B + QA routing for Paddle vs Stripe hosted Checkout.
 * Safe for SSR: defaults to paddle until client effects resolve.
 */
export const useCheckoutProvider = (): {
  checkoutProvider: CheckoutProvider;
  ready: boolean;
} => {
  const router = useRouter();
  const { subscription } = useSubscriptionContext();
  const { trackEvent } = useAnalytics();
  const flagRaw = useFeatureFlagVariantKey(CHECKOUT_PROVIDER_FLAG_KEY);

  const [sticky, setSticky] = useState<CheckoutProvider | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [flagWaitExpired, setFlagWaitExpired] = useState(false);
  const exposedRef = useRef(false);

  const urlOverride = useMemo(
    () => parseCheckoutProviderParam(router.query.checkout),
    [router.query.checkout]
  );

  useEffect(() => {
    setSticky(readStickyProvider());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!router.isReady) return;
    captureCheckoutPromoFromQuery(router.query);
  }, [router.isReady, router.query]);

  const checkoutProvider = resolveCheckoutProvider({
    subscription,
    urlOverride,
    sticky,
    flagVariant: flagRaw,
  });

  const flagReady = flagRaw !== undefined;
  const ready = isCheckoutProviderReady({
    hydrated,
    routerReady: router.isReady,
    flagReady,
    urlOverride,
    sticky,
    flagWaitExpired,
  });

  useEffect(() => {
    if (!hydrated || flagReady || urlOverride || sticky) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setFlagWaitExpired(true);
    }, CHECKOUT_PROVIDER_FLAG_WAIT_MS);
    return () => window.clearTimeout(timeout);
  }, [hydrated, flagReady, urlOverride, sticky]);

  useEffect(() => {
    if (!hydrated) return;

    if (
      shouldPersistCheckoutProvider({ subscription, urlOverride }) &&
      (urlOverride || flagReady || flagWaitExpired)
    ) {
      writeStickyProvider(checkoutProvider);
      setSticky(checkoutProvider);
    }

    posthogClient.register({
      [`$feature/${CHECKOUT_PROVIDER_FLAG_KEY}`]: checkoutProvider,
      checkout_provider: checkoutProvider,
    });

    if (!exposedRef.current && (flagReady || urlOverride || flagWaitExpired)) {
      exposedRef.current = true;
      trackEvent("Checkout Provider Exposed", {
        flag: CHECKOUT_PROVIDER_FLAG_KEY,
        variant: checkoutProvider,
        source: urlOverride
          ? "url"
          : sticky
            ? "sticky"
            : flagReady
              ? "posthog"
              : "default",
      });
    }
  }, [
    hydrated,
    checkoutProvider,
    subscription,
    urlOverride,
    flagReady,
    flagWaitExpired,
    sticky,
    trackEvent,
  ]);

  return { checkoutProvider, ready };
};
